import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Code, Users, MessageCircle, FileText, ExternalLink, Brain, Database, Zap } from 'lucide-react';

export const Resources: React.FC = () => {
  const documentationResources = [
    {
      title: "AI Fundamentals 2025",
      description: "Complete guide to modern AI including ChatGPT-5 and Agent Builder",
      icon: <Brain className="w-6 h-6" />,
      link: "/topics/ai-fundamentals"
    },
    {
      title: "GPT-models & AI-revolutie",
      description: "ChatGPT-5, Sora 2, en de nieuwste AI-ontwikkelingen van 2025",
      icon: <Zap className="w-6 h-6" />,
      link: "/topics/gpt-modellen-ai-revolutie"
    },
    {
      title: "AI-concurrentie & Modellen",
      description: "Gemini 2.0, Claude 4, Llama 4 en de strijd om AI-superioriteit",
      icon: <Database className="w-6 h-6" />,
      link: "/topics/concurrentie-ai-modellen"
    },
    {
      title: "Prompt Engineering 2025",
      description: "Effectieve prompts voor ChatGPT-5 en AI-agents",
      icon: <Code className="w-6 h-6" />,
      link: "/topics/prompt-engineering-best-practices"
    },
    {
      title: "AI-toepassingen & Toekomst",
      description: "Praktische implementaties en sectorale transformaties",
      icon: <FileText className="w-6 h-6" />,
      link: "/topics/toepassingen-toekomstperspectief"
    }
  ];

  // Consolidated, vetted sources used across AI, Digitale Geletterdheid en Mediawijsheid
  const externalResources = [
    // AI suite
    {
      title: "OpenAI Docs (ChatGPT, API, Assistants)",
      description: "Officiële documentatie van OpenAI over ChatGPT, Assistants en API-gebruik",
      url: "https://platform.openai.com/docs",
      icon: <ExternalLink className="w-4 h-4" />
    },
    {
      title: "Anthropic Docs (Claude)",
      description: "Documentatie voor Claude modellen en responsible AI-richtlijnen",
      url: "https://docs.anthropic.com/",
      icon: <ExternalLink className="w-4 h-4" />
    },
    {
      title: "Google AI (Gemini)",
      description: "Onderzoek en productdocumentatie rondom Gemini en Google AI",
      url: "https://ai.google/",
      icon: <ExternalLink className="w-4 h-4" />
    },

    // Digitale geletterdheid suite
    {
      title: "Digiwijzer – Wat is Digitale Geletterdheid?",
      description: "Uitleg, pijlers en kerndoelen rondom Digitale Geletterdheid in NL",
      url: "https://digiwijzer.nl/wat-is-digitale-geletterdheid/",
      icon: <ExternalLink className="w-4 h-4" />
    },
    {
      title: "SLO – Digitale Geletterdheid (kerndoelen)",
      description: "Kaders en materialen bij de (concept)kerndoelen Digitale Geletterdheid",
      url: "https://www.slo.nl/",
      icon: <ExternalLink className="w-4 h-4" />
    },

    // Mediawijsheid suite
    {
      title: "Netwerk Mediawijsheid",
      description: "Kaders, handreikingen en bronnen over mediawijsheid in Nederland",
      url: "https://www.mediawijsheid.nl/digitale-geletterdheid/",
      icon: <ExternalLink className="w-4 h-4" />
    },

    // Overkoepelende referenties
    {
      title: "Kennisnet – Handreikingen Digitale Geletterdheid",
      description: "Praktische handboeken en handreikingen voor scholen",
      url: "https://www.kennisnet.nl/onderwerpen/digitale-geletterdheid/",
      icon: <ExternalLink className="w-4 h-4" />
    },
    {
      title: "arXiv – AI research",
      description: "Recente publicaties en doorbraken op het gebied van AI en ML",
      url: "https://arxiv.org/list/cs.AI/recent",
      icon: <ExternalLink className="w-4 h-4" />
    }
  ];

  const communityResources = [
    {
      title: "OpenAI Community Forum",
      description: "Official community for ChatGPT-5 and OpenAI products",
      url: "https://community.openai.com/",
      icon: <Users className="w-4 h-4" />
    },
    {
      title: "AI Agents & Automation Discord",
      description: "Community for AI agents, automation, and Agent Builder discussions",
      url: "https://discord.gg/ai-agents",
      icon: <MessageCircle className="w-4 h-4" />
    },
    {
      title: "Prompt Engineering 2025",
      description: "Latest techniques and best practices for modern AI prompts",
      url: "https://www.reddit.com/r/PromptEngineering/",
      icon: <Code className="w-4 h-4" />
    },
    {
      title: "AI Video Generation Community",
      description: "Community for Sora 2, video AI, and creative AI tools",
      url: "https://discord.gg/ai-video",
      icon: <FileText className="w-4 h-4" />
    }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Back button */}
      <Link 
        to="/"
        className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Terug naar home
      </Link>

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Bronnen & Documentatie</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Overzicht van de bronnen die gebruikt zijn voor onze trainingssuites: AI, Digitale Geletterdheid en Mediawijsheid.
        </p>
      </div>

      {/* Internal Documentation */}
      <section className="mb-12">
        <div className="flex items-center mb-6">
          <BookOpen className="w-6 h-6 text-blue-600 mr-3" />
          <h2 className="text-2xl font-bold text-gray-900">Onze Training Modules</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documentationResources.map((resource, index) => (
            <Link
              key={index}
              to={resource.link}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow group"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mr-4 group-hover:bg-blue-200 transition-colors">
                  {resource.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {resource.title}
                </h3>
              </div>
              <p className="text-gray-600">{resource.description}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* External Resources */}
      <section className="mb-12">
        <div className="flex items-center mb-6">
          <FileText className="w-6 h-6 text-green-600 mr-3" />
          <h2 className="text-2xl font-bold text-gray-900">Externe Documentatie</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {externalResources.map((resource, index) => (
            <a
              key={index}
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors mb-2">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600">{resource.description}</p>
                </div>
                <div className="ml-4 text-gray-400 group-hover:text-green-600 transition-colors">
                  {resource.icon}
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Community Resources */}
      <section className="mb-12">
        <div className="flex items-center mb-6">
          <Users className="w-6 h-6 text-purple-600 mr-3" />
          <h2 className="text-2xl font-bold text-gray-900">Community & Support</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {communityResources.map((resource, index) => (
            <a
              key={index}
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 group-hover:bg-purple-200 transition-colors">
                  {resource.icon}
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors mb-2">
                {resource.title}
              </h3>
              <p className="text-gray-600">{resource.description}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Quick Links */}
      <section className="bg-gray-50 rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Snelle Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/topics"
            className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow group"
          >
            <BookOpen className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
              Alle Onderwerpen
            </span>
          </Link>
          <Link
            to="/training/summary"
            className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow group"
          >
            <Zap className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <span className="font-medium text-gray-900 group-hover:text-green-600 transition-colors">
              Verkorte Training
            </span>
          </Link>
          <Link
            to="/training/full"
            className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow group"
          >
            <Brain className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <span className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
              Volledige Training
            </span>
          </Link>
          <Link
            to="/progress"
            className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow group"
          >
            <Database className="w-8 h-8 text-orange-600 mx-auto mb-2" />
            <span className="font-medium text-gray-900 group-hover:text-orange-600 transition-colors">
              Mijn Voortgang
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
};
